/**
 * Minimal app state + event bus. Deliberately framework-free: the UI overlay
 * and the Cesium-driving modules both subscribe here, keeping the Viewer out of
 * any component render tree.
 */

export type MeasureMode = 'none' | 'distance' | 'height' | 'area';

export interface AppState {
  measureMode: MeasureMode;
  layers: {
    imagery: boolean;
    rail: boolean;
    buildings: boolean;
    roads: boolean;
    vegetation: boolean;
  };
  /** Free-form status line shown in the UI (terrain path chosen, errors, etc.). */
  status: string;
}

type Listener = (state: Readonly<AppState>) => void;

const state: AppState = {
  measureMode: 'none',
  layers: {
    imagery: true,
    rail: true,
    buildings: false,
    roads: false,
    vegetation: false,
  },
  status: '',
};

const listeners = new Set<Listener>();

export const store = {
  get(): Readonly<AppState> {
    return state;
  },

  set(patch: Partial<AppState>): void {
    Object.assign(state, patch);
    this.emit();
  },

  setLayer(name: keyof AppState['layers'], visible: boolean): void {
    state.layers[name] = visible;
    this.emit();
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    fn(state);
    return () => listeners.delete(fn);
  },

  emit(): void {
    for (const fn of listeners) fn(state);
  },
};
